/* eslint-disable */
import { Timestamp } from "../../../google/protobuf/timestamp";
import { TourTimeSlot } from "./tour_time_slot";

export const protobufPackage = "od_protos.touring.data";

export interface TourRequest {
  id: string;
  /** The address_id of the tour request, which will map to a tourable property. */
  addressId: string;
  /** The customer_id of the tour request, which will map to a touring customer. */
  customerId: string;
  /**
   * The showing id of the tour request, which is set when a showing has been
   * associated with the tour request, for tour request types that require
   * showings.
   */
  showingId: string;
  /** The state of the tour request */
  state: TourRequest_State;
  /** The type of tour request entity */
  tourType: TourRequest_TourType;
  /** The reason a tour request entity was terminated */
  terminationReason: TourRequest_TerminationReason;
  /**
   * TourTimeSlots are times that have been proposed (and accepted/rejected)
   * for a tour request
   */
  tourTimeSlots: TourTimeSlot[];
  /**
   * Flag to indicate whether a buyer's agent is expected to be present at the
   * tour
   *
   * @deprecated
   */
  buyersAgentExpectedAtTour?:
    | boolean
    | undefined;
  /**
   * JSON questions submitted by the touring customer when making the tour
   * request
   *
   * Example: "{ \"buyer_timelone\": "< 1 month", \"buyer_affordability\":
   * \"Can submit cash offer of offer list price or higher\" }"
   *
   * These questions are not meant to drive any tour request behavior, and are
   * meant to be immutable responses from the touring customer when submitting
   * a tour request, to be viewed by operators to help assist the touring
   * customer.
   */
  buyerQuestionnaireResponses: string;
  /** The status of a buyer's agent for tour attendance */
  buyersAgentStatus: TourRequest_BuyersAgentStatus;
  /** The notes recorded for the tour request */
  notes: string;
  /** The time when the tour request was created */
  createdAt?: Date | undefined;
}

/** TerminationReason indicates the reason a TourRequest entity was terminated */
export enum TourRequest_TerminationReason {
  UNKNOWN_TERMINATION_REASON = 0,
  /** NO_SHOW - The TouringCustomer was expected to attend the tour, but did not show up */
  NO_SHOW = 1,
  /** NO_FACILITATOR - The tour required a facilitator, however no facilitator was available */
  NO_FACILITATOR = 2,
  /**
   * TOURING_CUSTOMER_CANCELED - The touring customer canceled the tour request and the seller decided
   * not to keep the showing
   */
  TOURING_CUSTOMER_CANCELED = 3,
  /** SELLER_CANCELED - The seller canceled the tour request */
  SELLER_CANCELED = 4,
  /**
   * SELLER_REJECTED - The seller rejected all proposed tour time slots and did not propose any
   * alternative slots of their own
   */
  SELLER_REJECTED = 5,
  /**
   * VERIFICATION_FAILED - The touring customer failed the verification process and was unable to
   * tour the home
   */
  VERIFICATION_FAILED = 6,
  /** SHOWING_CANCELED - The showing was canceled and the tour was not completed */
  SHOWING_CANCELED = 7,
  /** DUPLICATE - The TourRequest was a duplicate request */
  DUPLICATE = 8,
  /** INVALID - The TourRequest was a invalid and not required */
  INVALID = 9,
  UNRECOGNIZED = -1,
}

export function tourRequest_TerminationReasonFromJSON(object: any): TourRequest_TerminationReason {
  switch (object) {
    case 0:
    case "UNKNOWN_TERMINATION_REASON":
      return TourRequest_TerminationReason.UNKNOWN_TERMINATION_REASON;
    case 1:
    case "NO_SHOW":
      return TourRequest_TerminationReason.NO_SHOW;
    case 2:
    case "NO_FACILITATOR":
      return TourRequest_TerminationReason.NO_FACILITATOR;
    case 3:
    case "TOURING_CUSTOMER_CANCELED":
      return TourRequest_TerminationReason.TOURING_CUSTOMER_CANCELED;
    case 4:
    case "SELLER_CANCELED":
      return TourRequest_TerminationReason.SELLER_CANCELED;
    case 5:
    case "SELLER_REJECTED":
      return TourRequest_TerminationReason.SELLER_REJECTED;
    case 6:
    case "VERIFICATION_FAILED":
      return TourRequest_TerminationReason.VERIFICATION_FAILED;
    case 7:
    case "SHOWING_CANCELED":
      return TourRequest_TerminationReason.SHOWING_CANCELED;
    case 8:
    case "DUPLICATE":
      return TourRequest_TerminationReason.DUPLICATE;
    case 9:
    case "INVALID":
      return TourRequest_TerminationReason.INVALID;
    case -1:
    case "UNRECOGNIZED":
    default:
      return TourRequest_TerminationReason.UNRECOGNIZED;
  }
}

export function tourRequest_TerminationReasonToJSON(object: TourRequest_TerminationReason): string {
  switch (object) {
    case TourRequest_TerminationReason.UNKNOWN_TERMINATION_REASON:
      return "UNKNOWN_TERMINATION_REASON";
    case TourRequest_TerminationReason.NO_SHOW:
      return "NO_SHOW";
    case TourRequest_TerminationReason.NO_FACILITATOR:
      return "NO_FACILITATOR";
    case TourRequest_TerminationReason.TOURING_CUSTOMER_CANCELED:
      return "TOURING_CUSTOMER_CANCELED";
    case TourRequest_TerminationReason.SELLER_CANCELED:
      return "SELLER_CANCELED";
    case TourRequest_TerminationReason.SELLER_REJECTED:
      return "SELLER_REJECTED";
    case TourRequest_TerminationReason.VERIFICATION_FAILED:
      return "VERIFICATION_FAILED";
    case TourRequest_TerminationReason.SHOWING_CANCELED:
      return "SHOWING_CANCELED";
    case TourRequest_TerminationReason.DUPLICATE:
      return "DUPLICATE";
    case TourRequest_TerminationReason.INVALID:
      return "INVALID";
    case TourRequest_TerminationReason.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** TourType indicates the type of tour request entity */
export enum TourRequest_TourType {
  UNKNOWN_TOUR_TYPE = 0,
  /** SHOWING_WALK_IN - The touring customer walks in to the property during a showing */
  SHOWING_WALK_IN = 1,
  /** SHOWING_REQUEST - The touring customer requests a showing ahead of time */
  SHOWING_REQUEST = 2,
  /** SHOWING_RSVP - The touring customer RSVPs to an already scheduled showing */
  SHOWING_RSVP = 3,
  /** SELF_TOUR_INSTANT - The touring customer shows up and tours a self-tourable home instantly */
  SELF_TOUR_INSTANT = 4,
  /** SELF_TOUR_SCHEDULED - The touring customer schedules a self-tour for a later time */
  SELF_TOUR_SCHEDULED = 5,
  /** SHOWINGTIME_SCHEDULED - An external agent schedules a tour via ShowingTime for a later time */
  SHOWINGTIME_SCHEDULED = 6,
  /** VENDOR_VISIT - A vendor visits the home for a service or inspection */
  VENDOR_VISIT = 7,
  UNRECOGNIZED = -1,
}

export function tourRequest_TourTypeFromJSON(object: any): TourRequest_TourType {
  switch (object) {
    case 0:
    case "UNKNOWN_TOUR_TYPE":
      return TourRequest_TourType.UNKNOWN_TOUR_TYPE;
    case 1:
    case "SHOWING_WALK_IN":
      return TourRequest_TourType.SHOWING_WALK_IN;
    case 2:
    case "SHOWING_REQUEST":
      return TourRequest_TourType.SHOWING_REQUEST;
    case 3:
    case "SHOWING_RSVP":
      return TourRequest_TourType.SHOWING_RSVP;
    case 4:
    case "SELF_TOUR_INSTANT":
      return TourRequest_TourType.SELF_TOUR_INSTANT;
    case 5:
    case "SELF_TOUR_SCHEDULED":
      return TourRequest_TourType.SELF_TOUR_SCHEDULED;
    case 6:
    case "SHOWINGTIME_SCHEDULED":
      return TourRequest_TourType.SHOWINGTIME_SCHEDULED;
    case 7:
    case "VENDOR_VISIT":
      return TourRequest_TourType.VENDOR_VISIT;
    case -1:
    case "UNRECOGNIZED":
    default:
      return TourRequest_TourType.UNRECOGNIZED;
  }
}

export function tourRequest_TourTypeToJSON(object: TourRequest_TourType): string {
  switch (object) {
    case TourRequest_TourType.UNKNOWN_TOUR_TYPE:
      return "UNKNOWN_TOUR_TYPE";
    case TourRequest_TourType.SHOWING_WALK_IN:
      return "SHOWING_WALK_IN";
    case TourRequest_TourType.SHOWING_REQUEST:
      return "SHOWING_REQUEST";
    case TourRequest_TourType.SHOWING_RSVP:
      return "SHOWING_RSVP";
    case TourRequest_TourType.SELF_TOUR_INSTANT:
      return "SELF_TOUR_INSTANT";
    case TourRequest_TourType.SELF_TOUR_SCHEDULED:
      return "SELF_TOUR_SCHEDULED";
    case TourRequest_TourType.SHOWINGTIME_SCHEDULED:
      return "SHOWINGTIME_SCHEDULED";
    case TourRequest_TourType.VENDOR_VISIT:
      return "VENDOR_VISIT";
    case TourRequest_TourType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum TourRequest_State {
  UNKNOWN_STATE = 0,
  /**
   * PENDING - The tour request has been initiated by a TouringCustomer and
   * TourTimeSlots have been proposed, but not yet accepted.
   */
  PENDING = 1,
  /**
   * PENDING_VERIFICATION - A tour request has been set up for an accepted timeslot, but needs
   * verification of the TouringCustomer (via IDV or other means) before the
   * TouringCustomer will be allowed to enter the home.
   */
  PENDING_VERIFICATION = 2,
  /**
   * SCHEDULED - A Tour Time Slot has been agreed upon between the TouringCustomer and
   * the Seller, and the TouringCustomer has been verified (if necessary)
   */
  SCHEDULED = 3,
  /**
   * COMPLETED - The tour request has been completed successfully (the TouringCustomer
   * has toured the home)
   */
  COMPLETED = 4,
  /**
   * TERMINATED - The tour request has been terminated unsuccessfully (the TouringCustomer
   * did not tour the home)
   */
  TERMINATED = 5,
  UNRECOGNIZED = -1,
}

export function tourRequest_StateFromJSON(object: any): TourRequest_State {
  switch (object) {
    case 0:
    case "UNKNOWN_STATE":
      return TourRequest_State.UNKNOWN_STATE;
    case 1:
    case "PENDING":
      return TourRequest_State.PENDING;
    case 2:
    case "PENDING_VERIFICATION":
      return TourRequest_State.PENDING_VERIFICATION;
    case 3:
    case "SCHEDULED":
      return TourRequest_State.SCHEDULED;
    case 4:
    case "COMPLETED":
      return TourRequest_State.COMPLETED;
    case 5:
    case "TERMINATED":
      return TourRequest_State.TERMINATED;
    case -1:
    case "UNRECOGNIZED":
    default:
      return TourRequest_State.UNRECOGNIZED;
  }
}

export function tourRequest_StateToJSON(object: TourRequest_State): string {
  switch (object) {
    case TourRequest_State.UNKNOWN_STATE:
      return "UNKNOWN_STATE";
    case TourRequest_State.PENDING:
      return "PENDING";
    case TourRequest_State.PENDING_VERIFICATION:
      return "PENDING_VERIFICATION";
    case TourRequest_State.SCHEDULED:
      return "SCHEDULED";
    case TourRequest_State.COMPLETED:
      return "COMPLETED";
    case TourRequest_State.TERMINATED:
      return "TERMINATED";
    case TourRequest_State.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** BuyersAgentStatus indicates a buyer's agent status for Tour attendance */
export enum TourRequest_BuyersAgentStatus {
  UNKNOWN_BUYERS_AGENT_STATUS = 0,
  BUYERS_AGENT_ATTENDING = 1,
  BUYERS_AGENT_NOT_ATTENDING = 2,
  UNRECOGNIZED = -1,
}

export function tourRequest_BuyersAgentStatusFromJSON(object: any): TourRequest_BuyersAgentStatus {
  switch (object) {
    case 0:
    case "UNKNOWN_BUYERS_AGENT_STATUS":
      return TourRequest_BuyersAgentStatus.UNKNOWN_BUYERS_AGENT_STATUS;
    case 1:
    case "BUYERS_AGENT_ATTENDING":
      return TourRequest_BuyersAgentStatus.BUYERS_AGENT_ATTENDING;
    case 2:
    case "BUYERS_AGENT_NOT_ATTENDING":
      return TourRequest_BuyersAgentStatus.BUYERS_AGENT_NOT_ATTENDING;
    case -1:
    case "UNRECOGNIZED":
    default:
      return TourRequest_BuyersAgentStatus.UNRECOGNIZED;
  }
}

export function tourRequest_BuyersAgentStatusToJSON(object: TourRequest_BuyersAgentStatus): string {
  switch (object) {
    case TourRequest_BuyersAgentStatus.UNKNOWN_BUYERS_AGENT_STATUS:
      return "UNKNOWN_BUYERS_AGENT_STATUS";
    case TourRequest_BuyersAgentStatus.BUYERS_AGENT_ATTENDING:
      return "BUYERS_AGENT_ATTENDING";
    case TourRequest_BuyersAgentStatus.BUYERS_AGENT_NOT_ATTENDING:
      return "BUYERS_AGENT_NOT_ATTENDING";
    case TourRequest_BuyersAgentStatus.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

function createBaseTourRequest(): TourRequest {
  return {
    id: "",
    addressId: "",
    customerId: "",
    showingId: "",
    state: 0,
    tourType: 0,
    terminationReason: 0,
    tourTimeSlots: [],
    buyersAgentExpectedAtTour: undefined,
    buyerQuestionnaireResponses: "",
    buyersAgentStatus: 0,
    notes: "",
    createdAt: undefined,
  };
}

export const TourRequest = {
  fromJSON(object: any): TourRequest {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      addressId: isSet(object.addressId) ? String(object.addressId) : "",
      customerId: isSet(object.customerId) ? String(object.customerId) : "",
      showingId: isSet(object.showingId) ? String(object.showingId) : "",
      state: isSet(object.state) ? tourRequest_StateFromJSON(object.state) : 0,
      tourType: isSet(object.tourType) ? tourRequest_TourTypeFromJSON(object.tourType) : 0,
      terminationReason: isSet(object.terminationReason)
        ? tourRequest_TerminationReasonFromJSON(object.terminationReason)
        : 0,
      tourTimeSlots: Array.isArray(object?.tourTimeSlots)
        ? object.tourTimeSlots.map((e: any) => TourTimeSlot.fromJSON(e))
        : [],
      buyersAgentExpectedAtTour: isSet(object.buyersAgentExpectedAtTour)
        ? Boolean(object.buyersAgentExpectedAtTour)
        : undefined,
      buyerQuestionnaireResponses: isSet(object.buyerQuestionnaireResponses)
        ? String(object.buyerQuestionnaireResponses)
        : "",
      buyersAgentStatus: isSet(object.buyersAgentStatus)
        ? tourRequest_BuyersAgentStatusFromJSON(object.buyersAgentStatus)
        : 0,
      notes: isSet(object.notes) ? String(object.notes) : "",
      createdAt: isSet(object.createdAt) ? fromJsonTimestamp(object.createdAt) : undefined,
    };
  },

  toJSON(message: TourRequest): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.addressId !== "") {
      obj.addressId = message.addressId;
    }
    if (message.customerId !== "") {
      obj.customerId = message.customerId;
    }
    if (message.showingId !== "") {
      obj.showingId = message.showingId;
    }
    if (message.state !== 0) {
      obj.state = tourRequest_StateToJSON(message.state);
    }
    if (message.tourType !== 0) {
      obj.tourType = tourRequest_TourTypeToJSON(message.tourType);
    }
    if (message.terminationReason !== 0) {
      obj.terminationReason = tourRequest_TerminationReasonToJSON(message.terminationReason);
    }
    if (message.tourTimeSlots?.length) {
      obj.tourTimeSlots = message.tourTimeSlots.map((e) => TourTimeSlot.toJSON(e));
    }
    if (message.buyersAgentExpectedAtTour !== undefined) {
      obj.buyersAgentExpectedAtTour = message.buyersAgentExpectedAtTour;
    }
    if (message.buyerQuestionnaireResponses !== "") {
      obj.buyerQuestionnaireResponses = message.buyerQuestionnaireResponses;
    }
    if (message.buyersAgentStatus !== 0) {
      obj.buyersAgentStatus = tourRequest_BuyersAgentStatusToJSON(message.buyersAgentStatus);
    }
    if (message.notes !== "") {
      obj.notes = message.notes;
    }
    if (message.createdAt !== undefined) {
      obj.createdAt = message.createdAt.toISOString();
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<TourRequest>, I>>(base?: I): TourRequest {
    return TourRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<TourRequest>, I>>(object: I): TourRequest {
    const message = createBaseTourRequest();
    message.id = object.id ?? "";
    message.addressId = object.addressId ?? "";
    message.customerId = object.customerId ?? "";
    message.showingId = object.showingId ?? "";
    message.state = object.state ?? 0;
    message.tourType = object.tourType ?? 0;
    message.terminationReason = object.terminationReason ?? 0;
    message.tourTimeSlots = object.tourTimeSlots?.map((e) => TourTimeSlot.fromPartial(e)) || [];
    message.buyersAgentExpectedAtTour = object.buyersAgentExpectedAtTour ?? undefined;
    message.buyerQuestionnaireResponses = object.buyerQuestionnaireResponses ?? "";
    message.buyersAgentStatus = object.buyersAgentStatus ?? 0;
    message.notes = object.notes ?? "";
    message.createdAt = object.createdAt ?? undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends { $case: string } ? { [K in keyof Omit<T, "$case">]?: DeepPartial<T[K]> } & { $case: T["$case"] }
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function fromTimestamp(t: Timestamp): Date {
  let millis = (t.seconds || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof Date) {
    return o;
  } else if (typeof o === "string") {
    return new Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
